import { all, call, put, takeEvery } from 'redux-saga/effects'
import { AsyncAction } from 'src/utils/reduxUtils'
import {
  UploadVaccination,
  FetchDriver,
  UploadInsuranceBeneficiary,
  UploadVehicleInsurance,
  UploadInsuranceDeclaration,
  UploadVehicleRegistration,
  UploadMVR,
  UploadForm,
  UploadBGC,
  UploadEIN,
  UploadInsuranceCoverage,
} from 'src/constants/actionTypes'
import { ApiService, callClientSecureApi } from './api/callApi'
import { GcsFileReferenceDto, uploadFileToGcs } from './api/gcsUpload'
import { getApiErrorMessage } from 'src/utils/errorHandlers'
import { Endpoints } from 'src/constants/endpoints'
import { InsuranceBeneficiaryRelation } from 'src/constants/insuranceBeneficiary'
import { UploadFormResponse } from 'src/interfaces/collectDataForm'
import { captureSentryError } from 'src/utils/sentry'

interface UploadFormRequest {
  data: Array<{ id: string; value: any }>
  files: Array<{ id: string; value: File }>
  stepId: string
}

function* onUploadForm(action: AsyncAction<UploadFormRequest>) {
  const { data, stepId, files } = action.payload

  try {
    let fileRefs: GcsFileReferenceDto[] = []

    if (files.length) {
      fileRefs = (yield all(
        files.map((item) =>
          call(
            uploadFileToGcs,
            {
              stepId,
              originalname: item.value.name,
              mimeType: item.value.type,
              size: item.value.size,
            },
            item.value,
            item.id,
          ),
        ),
      )) as GcsFileReferenceDto[]
    }

    const { payload }: { payload: UploadFormResponse } = yield call(
      callClientSecureApi,
      Endpoints.UploadForm,
      {
        method: 'POST',
        apiService: ApiService.Paraworks,
        body: {
          stepId,
          data,
          files: fileRefs,
        },
      },
    )

    yield put(UploadForm.success())
    yield put(FetchDriver.request())

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    captureSentryError(err, {
      message: '[saga.documents.onUploadForm] error',
    })

    const errorMessage = getApiErrorMessage(err, 'Failed to upload form')

    yield put(UploadForm.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface UploadVehicleInsuranceRequest {
  stepId: string
  file?: File
  policyDocument?: File
  type: string
  year?: string | number
  carMake?: string
  carModel?: string
  vin?: string | number
}

function* onUploadVehicleInsurance(action: AsyncAction<UploadVehicleInsuranceRequest>) {
  const { file, type, year, carMake, carModel, vin, stepId, policyDocument } = action.payload

  try {
    let fileRef: GcsFileReferenceDto | undefined
    let policyDocumentRef: GcsFileReferenceDto | undefined

    if (file) {
      fileRef = (yield call(
        uploadFileToGcs,
        {
          stepId,
          originalname: file.name,
          mimeType: file.type,
          size: file.size,
        },
        file,
      )) as GcsFileReferenceDto
    }

    if (policyDocument) {
      policyDocumentRef = (yield call(
        uploadFileToGcs,
        {
          stepId,
          originalname: policyDocument.name,
          mimeType: policyDocument.type,
          size: policyDocument.size,
        },
        policyDocument,
      )) as GcsFileReferenceDto
    }

    yield call(callClientSecureApi, Endpoints.UploadVehicleInsurance, {
      method: 'POST',
      apiService: ApiService.Paraworks,
      body: {
        stepId,
        type,
        ...(year !== undefined && { year }),
        ...(carMake !== undefined && { carMake }),
        ...(carModel !== undefined && { carModel }),
        ...(vin !== undefined && { vin }),
        ...(fileRef && { file: fileRef }),
        ...(policyDocumentRef && { policyDocument: policyDocumentRef }),
      },
    })

    yield put(UploadVehicleInsurance.success())
    yield put(FetchDriver.request())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    captureSentryError(err, {
      message: '[saga.documents.onUploadVehicleInsurance] error',
    })

    const errorMessage = getApiErrorMessage(err, 'Failed to upload vehicle insurance')

    yield put(UploadVehicleInsurance.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface UploadInsuranceDeclarationRequest {
  files: File[]
  stepId: string
}

function* onUploadInsuranceDeclaration(action: AsyncAction<UploadInsuranceDeclarationRequest>) {
  const { files, stepId } = action.payload

  try {
    const fileRefs = (yield all(
      files.map((f) =>
        call(uploadFileToGcs, { stepId, originalname: f.name, mimeType: f.type, size: f.size }, f),
      ),
    )) as GcsFileReferenceDto[]

    yield call(callClientSecureApi, Endpoints.UploadInsuranceDeclaration, {
      method: 'POST',
      apiService: ApiService.Paraworks,
      body: { stepId, files: fileRefs },
    })

    yield put(UploadInsuranceDeclaration.success())
    yield put(FetchDriver.request())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    captureSentryError(err, {
      message: '[saga.documents.onUploadInsuranceDeclaration] error',
    })

    const errorMessage = getApiErrorMessage(err, 'Failed to upload insurance declaration')

    yield put(UploadInsuranceDeclaration.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface UploadVehicleRegistrationRequest {
  file: File
  stepId: string
}

function* onUploadVehicleRegistration(action: AsyncAction<UploadVehicleRegistrationRequest>) {
  const { file, stepId } = action.payload

  try {
    const fileRef = (yield call(
      uploadFileToGcs,
      {
        stepId,
        originalname: file.name,
        mimeType: file.type,
        size: file.size,
      },
      file,
    )) as GcsFileReferenceDto

    yield call(callClientSecureApi, Endpoints.UploadVehicleRegistration, {
      method: 'POST',
      apiService: ApiService.Paraworks,
      body: { stepId, file: fileRef },
    })

    yield put(UploadVehicleRegistration.success())
    yield put(FetchDriver.request())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    captureSentryError(err, {
      message: '[saga.documents.onUploadVehicleRegistration] error',
    })

    const errorMessage = getApiErrorMessage(err, 'Failed to upload vehicle registration')

    yield put(UploadVehicleRegistration.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface UploadVaccinationRequest {
  brand?: string
  date?: string
  isVaccinated: boolean
  stepId: string
}

function* onUploadVaccination(action: AsyncAction<UploadVaccinationRequest>) {
  const { brand, date, isVaccinated, stepId } = action.payload

  try {
    const body: UploadVaccinationRequest = {
      isVaccinated,
      stepId,
    }

    if (isVaccinated && brand && date) {
      body.brand = brand
      body.date = date
    }

    yield call(callClientSecureApi, Endpoints.UploadVaccination, {
      method: 'POST',
      apiService: ApiService.Paraworks,
      body,
    })

    yield put(UploadVaccination.success())
    yield put(FetchDriver.request())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    captureSentryError(err, {
      message: '[saga.documents.onUploadVaccination] error',
    })

    const errorMessage = getApiErrorMessage(err, 'Failed to upload vaccination')

    yield put(UploadVaccination.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface UploadInsuranceBeneficiaryRequest {
  firstName: string
  lastName: string
  relation: InsuranceBeneficiaryRelation
  stepId: string
}

function* onUploadInsuranceBeneficiary(action: AsyncAction<UploadInsuranceBeneficiaryRequest>) {
  const { firstName, lastName, relation, stepId } = action.payload

  try {
    yield call(callClientSecureApi, Endpoints.UploadInsuranceBeneficiary, {
      method: 'POST',
      apiService: ApiService.Paraworks,
      body: {
        firstName,
        lastName,
        relation,
        stepId,
      },
    })

    yield put(UploadInsuranceBeneficiary.success())
    yield put(FetchDriver.request())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    captureSentryError(err, {
      message: '[saga.documents.onUploadInsuranceBeneficiary] error',
    })

    const errorMessage = getApiErrorMessage(err, 'Failed to upload insurance beneficiary')

    yield put(UploadInsuranceBeneficiary.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface UploadMVRRequest {
  file?: File
  stepId: string
}

function* onUploadMVR(action: AsyncAction<UploadMVRRequest>) {
  const { file, stepId } = action.payload

  try {
    let fileRef: GcsFileReferenceDto | undefined

    if (file) {
      fileRef = (yield call(
        uploadFileToGcs,
        {
          stepId,
          originalname: file.name,
          mimeType: file.type,
          size: file.size,
        },
        file,
      )) as GcsFileReferenceDto
    }

    yield call(callClientSecureApi, Endpoints.UploadMVR, {
      method: 'POST',
      apiService: ApiService.Paraworks,
      body: {
        stepId,
        ...(fileRef && { file: fileRef }),
      },
    })

    yield put(UploadMVR.success())
    yield put(FetchDriver.request())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    captureSentryError(err, {
      message: '[saga.documents.onUploadMVR] error',
    })

    const errorMessage = getApiErrorMessage(err, 'Failed to upload mvr')

    yield put(UploadMVR.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface UploadBGCRequest {
  files: Array<{ file: File; name: string }>
  stepId: string
}

function* onUploadBGC(action: AsyncAction<UploadBGCRequest>) {
  const { files, stepId } = action.payload

  try {
    if (!files.length) {
      throw new Error('At least one file is required for background check upload')
    }

    let fileRefs: GcsFileReferenceDto[] = []

    if (files.length) {
      fileRefs = (yield all(
        files.map((item) =>
          call(
            uploadFileToGcs,
            {
              stepId,
              originalname: item.file.name,
              mimeType: item.file.type,
              size: item.file.size,
            },
            item.file,
            item.name,
          ),
        ),
      )) as GcsFileReferenceDto[]
    }

    yield call(callClientSecureApi, Endpoints.UploadBGC, {
      method: 'POST',
      apiService: ApiService.Paraworks,
      body: { stepId, files: fileRefs },
    })

    yield put(UploadBGC.success())
    yield put(FetchDriver.request())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    captureSentryError(err, {
      message: '[saga.documents.onUploadBGC] error',
    })

    const errorMessage = getApiErrorMessage(err, 'Failed to upload bgc')

    yield put(UploadBGC.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface UploadEINRequest {
  stepId: string
  file?: File
  ein?: string | number
  businessName?: string
  streetAddress?: string
  city?: string
  state?: string
  zipCode?: string | number
  isBusinessEntityFlow?: boolean
}

function* onUploadEIN(action: AsyncAction<UploadEINRequest>) {
  const {
    file,
    ein,
    zipCode,
    stepId,
    businessName,
    streetAddress,
    city,
    state,
    isBusinessEntityFlow,
  } = action.payload

  try {
    let fileRef: GcsFileReferenceDto | undefined

    if (file) {
      fileRef = (yield call(
        uploadFileToGcs,
        {
          stepId,
          originalname: file.name,
          mimeType: file.type,
          size: file.size,
        },
        file,
      )) as GcsFileReferenceDto
    }

    yield call(callClientSecureApi, Endpoints.UploadEIN, {
      method: 'POST',
      apiService: ApiService.Paraworks,
      body: {
        stepId,
        isBusinessEntityFlow,
        ...(fileRef !== undefined && { file: fileRef }),
        ...(ein !== undefined && { ein }),
        ...(zipCode !== undefined && { zipCode }),
        ...(businessName !== undefined && { businessName }),
        ...(streetAddress !== undefined && { streetAddress }),
        ...(city !== undefined && { city }),
        ...(state !== undefined && { state }),
      },
    })

    yield put(UploadEIN.success())
    yield put(FetchDriver.request())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    captureSentryError(err, {
      message: '[saga.documents.onUploadEIN] error',
    })

    const errorMessage = getApiErrorMessage(err, 'Failed to upload ein')

    yield put(UploadEIN.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

export interface UploadInsuranceCoverageRequest {
  stepId: string
  files?: File[]
  optOut: boolean
}

function* onUploadInsuranceCoverage(action: AsyncAction<UploadInsuranceCoverageRequest>) {
  const { files, optOut, stepId } = action.payload

  try {
    let fileRefs: GcsFileReferenceDto[] | undefined

    if (files && files.length > 0) {
      fileRefs = (yield all(
        files.map((f) =>
          call(
            uploadFileToGcs,
            { stepId, originalname: f.name, mimeType: f.type, size: f.size },
            f,
          ),
        ),
      )) as GcsFileReferenceDto[]
    }

    yield call(callClientSecureApi, Endpoints.UploadInsuranceCoverage, {
      method: 'POST',
      apiService: ApiService.Paraworks,
      body: {
        stepId,
        optOut,
        ...(fileRefs && { files: fileRefs }),
      },
    })

    yield put(UploadInsuranceCoverage.success())
    yield put(FetchDriver.request())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    captureSentryError(err, {
      message: '[saga.documents.onUploadInsuranceCoverage] error',
    })

    const errorMessage = getApiErrorMessage(err, 'Failed to upload insurance')

    yield put(UploadInsuranceCoverage.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

export function* documentsSaga() {
  yield takeEvery(UploadForm.type.REQUEST, onUploadForm)
  yield takeEvery(UploadVehicleInsurance.type.REQUEST, onUploadVehicleInsurance)
  yield takeEvery(UploadInsuranceDeclaration.type.REQUEST, onUploadInsuranceDeclaration)
  yield takeEvery(UploadVehicleRegistration.type.REQUEST, onUploadVehicleRegistration)
  yield takeEvery(UploadVaccination.type.REQUEST, onUploadVaccination)
  yield takeEvery(UploadInsuranceBeneficiary.type.REQUEST, onUploadInsuranceBeneficiary)
  yield takeEvery(UploadMVR.type.REQUEST, onUploadMVR)
  yield takeEvery(UploadBGC.type.REQUEST, onUploadBGC)
  yield takeEvery(UploadEIN.type.REQUEST, onUploadEIN)
  yield takeEvery(UploadInsuranceCoverage.type.REQUEST, onUploadInsuranceCoverage)
}
